import { BusinessChallenges } from '@platform/client';
import { BusinessChallenge } from '@platform/client/schemas';
import { Icon, PillBadge } from '@platform/components';
import cx from 'classix';
import { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { DeleteChallengeModal } from './DeleteChallengeModal';
import { Link } from 'wouter';
import { businessChallengeFullText } from '../../util/TextHelpers';

const maxChallenges = 3;

const BusinessChallengesSection = ({ organizationId }: { organizationId: number }) => {
  const [deleteChallengeNumber, setDeleteChallengeNumber] = useState<number>();
  const {
    data: challengeData,
    isSuccess,
    isLoading,
  } = BusinessChallenges.useGetOrganizationBusinessChallenges(organizationId);
  const existingChallenges = (challengeData?.challenges ?? []).filter(
    (challenge) => challenge.challenge && challenge.challengeContext,
  );
  return (
    <div className='mt-8 mb-16 text-gray-700'>
      <ChallengesHeader completed={existingChallenges.length} />
      <ChallengeCards
        organizationId={organizationId}
        challenges={challengeData?.challenges ?? []}
        isLoading={isLoading}
        isSuccess={isSuccess}
        setDeleteChallengeNumber={setDeleteChallengeNumber}
      />
      {deleteChallengeNumber && (
        <DeleteChallengeModal
          isOpen={true}
          onClose={() => setDeleteChallengeNumber(undefined)}
          challengeNumber={deleteChallengeNumber}
          organizationId={organizationId}
        />
      )}
    </div>
  );
};

const ChallengesHeader = ({ completed }: { completed: number }) => {
  return (
    <div className='w-full xl:w-[848px] flex gap-2 mb-8'>
      <div className='flex-grow'>
        <h2 className='text-display-xs break-words max-w-[75%] lg:max-w-full font-medium mb-2'>
          Your business challenges
        </h2>
        <p className='text-lg'>
          Update your current priorities so we can match you to the best opportunities.{' '}
        </p>
      </div>
      <div className='w-16 flex flex-col items-end'>
        <h2 className='text-display-xs font-bold text-brand-500'>
          {completed}/{maxChallenges}
        </h2>
        <p className='text-xs text-gray-500'>Completed</p>
      </div>
    </div>
  );
};

const ChallengeCards = ({
  organizationId,
  challenges,
  isLoading,
  isSuccess,
  setDeleteChallengeNumber,
}: {
  organizationId: number;
  challenges: BusinessChallenge[];
  isLoading: boolean;
  isSuccess: boolean;
  setDeleteChallengeNumber: React.Dispatch<React.SetStateAction<number | undefined>>;
}) => {
  return (
    <div className='flex flex-col items-center lg:flex-row lg:justify-start flex-wrap gap-6'>
      {isLoading &&
        [1, 2, 3].map((challengeNumber) => (
          <ChallengeCardBackground key={challengeNumber}>
            <Skeleton height={190} />
          </ChallengeCardBackground>
        ))}
      {isSuccess &&
        challenges.map((challenge) => (
          <ChallengeCard
            key={challenge.challengeNumber}
            organizationId={organizationId}
            challenge={challenge}
            setDeleteChallengeNumber={setDeleteChallengeNumber}
          />
        ))}
    </div>
  );
};

const ChallengeCard = ({
  organizationId,
  challenge,
  setDeleteChallengeNumber,
}: {
  organizationId: number;
  setDeleteChallengeNumber: React.Dispatch<React.SetStateAction<number | undefined>>;
  challenge: BusinessChallenge;
}) => {
  return (
    <>
      {challenge && challenge.challenge && challenge.challengeContext ?
        <ChallengeCardBackground>
          <div className='flex justify-between items-center py-1'>
            <PillBadge variant='plain' color='secondary-brand-50' size='md'>
              Challenge {challenge.challengeNumber}
            </PillBadge>
            <div className='flex gap-7 lg:gap-5'>
              <Link
                to={`~/platform/business-challenges/${organizationId}/${challenge.challengeNumber}`}
                aria-label={`Edit challenge ${challenge.challengeNumber}`}
              >
                <Icon additionalClassNames='hidden md:block' type='edit-01' size='20' />
                <Icon additionalClassNames='block md:hidden' type='edit-01' size='20' />
              </Link>
              <button
                aria-label='Delete challenge'
                onClick={() => setDeleteChallengeNumber(challenge.challengeNumber)}
              >
                <Icon type='trash-01' size='20' additionalClassNames='text-error-600' />
              </button>
            </div>
          </div>
          {/* truncate on full screen when cards are smaller */}
          <p className='line-clamp-1 font-semibold'>
            {businessChallengeFullText(challenge.challenge, true)}
          </p>
          <p className='text-sm wrap-break-word line-clamp-4'>{challenge.challengeContext}</p>
        </ChallengeCardBackground>
      : <ChallengeCardBlank
          challengeNumber={challenge?.challengeNumber}
          organizationId={organizationId}
        />
      }
    </>
  );
};

const ChallengeCardBlank = ({
  challengeNumber,
  organizationId,
}: {
  challengeNumber: number;
  organizationId: number;
}) => {
  return (
    <ChallengeCardBackground isBlank={true}>
      <Link
        className='h-full w-full flex flex-col items-center justify-center gap-2'
        to={`~/platform/business-challenges/${organizationId}/${challengeNumber}`}
        aria-label={`Add challenge ${challengeNumber}`}
      >
        <div className='bg-secondary-brand-50 h-12 w-12 rounded-full flex items-center justify-center'>
          <Icon type='plus' size='24' additionalClassNames='text-gray-400' />
        </div>
        <p>Add Challenge {challengeNumber}</p>
      </Link>
    </ChallengeCardBackground>
  );
};

const ChallengeCardBackground = ({
  isBlank = false,
  children,
}: {
  isBlank?: boolean;
  children: React.ReactNode;
}) => {
  return (
    <div
      className={cx(
        isBlank ? 'bg-white' : 'bg-secondary-brand-25 p-4',
        'w-full md:w-[268px] shadow-xs border-2 border-gray-200 border-dashed rounded-[14px] flex flex-col gap-2 h-48',
      )}
    >
      {children}
    </div>
  );
};

export default BusinessChallengesSection;
